import icons from "@vse/icons";
import widgets from "@vse/legacy/widgets";
import { Library, InstructionCategory, MachinelogicLibrary } from "@vse/machinelogic/types";

const isValidRobotConfig = (library: Library) => {
  const robotConfigs = library.getConfigManager().listRobots();
  return robotConfigs.some((robotConfig) => robotConfig.getType?.());
};

const isChildSequenceAvailableForParallelCommand = (library: MachinelogicLibrary) => {
  const currentApplication = library.currentApplication;
  const isCurrentSequenceMain = currentApplication.getInitialSequence().getName() === currentApplication.currentSequence.getName();
  const currentApplicationSequenceCount = currentApplication.listSequences().length;

  return currentApplicationSequenceCount > 2 ||
    (currentApplicationSequenceCount == 2 && isCurrentSequenceMain);
};

const addInstructionToCurrentSequence = (category: InstructionCategory, library: MachinelogicLibrary, instructionName?: string) => {
  const currentApplication = library.currentApplication;
  const currentSequence = currentApplication.currentSequence;

  if (!instructionName) {
    instructionName = Object.keys(library.getInstructionSet()[category])[0];
  }

  const currentInstruction = currentSequence.selectedInstruction;
  let instructionIndex;
  if (currentInstruction !== null) {
    instructionIndex = currentSequence.getInstructionIndex(currentInstruction) + 1;
  }

  currentSequence.newInstruction(category, instructionName, instructionIndex);
};

export interface ButtonConfig {
  groupName: string;
  category: InstructionCategory;
  tooltip: string;
  text: string;
  disabled?: boolean|((library: MachinelogicLibrary) => boolean);
  icon: string;
  className?: string;
  onClick: (item: ButtonConfig, library: MachinelogicLibrary) => void;
}

const baseCommandsButtonConfig: ButtonConfig[] = [
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.MOTOR,
    tooltip: 'Move',
    text: 'Add Motion',
    icon: icons.addMotor,
    disabled: (library) => library.getConfigManager().numberOfAxes() === 0,
    onClick: (item, library) => {
      let instructionName;
      if (library.getConfigManager().numberOfRealAxes() == 0) {
        // When only pneumatic actuators are configured, override the default instruction
        // wanted to use _getAxisCompatibleActionSet but open, close, push and pull are all acceptable options.
        const axis = library.getConfigManager().listAxes()[0];
        const axisType = axis && axis.type;
        if(axisType === "pneumatic_bag_gripper") {
          instructionName = 'close';
        } else if(axisType === "ac_motor_with_vfd") {
          instructionName = 'moveVFD';
        }
        else {
          instructionName = 'push';
        }
      }
      addInstructionToCurrentSequence(item.category, library, instructionName);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.WAIT_FOR,
    tooltip: 'Wait for ...',
    text: 'Add Wait',
    icon: icons.addWait,
    onClick: (item, library) => addInstructionToCurrentSequence(item.category, library)
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.OUTPUT,
    tooltip: 'Activate device',
    text: 'Add Output',
    icon: icons.addOutput,
    onClick: (item, library) => {
      let instructionName;
      // Default to a generateEvent if no output is defined.
      if (library.getConfigManager().listOutputs().length == 0) {
        instructionName = 'generateEvent';
      }

      addInstructionToCurrentSequence(item.category, library, instructionName);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.FSM,
    tooltip: 'State machine',
    text: 'Add State Machine',
    icon: icons.addFsm,
    onClick: (item, library) => {
      const currentApplication = library.currentApplication;
      const currentSequence = currentApplication.currentSequence;
      // Only allow this in the main sequence for the time being
      // (not that it wouldn't work elsewhere but to keep things simple)
      const isCurrentSequenceMain = currentApplication.getInitialSequence().getName() === currentSequence.getName();
      if (!isCurrentSequenceMain) {
        widgets.messageBox('FSM declarations are only allowed in the main sequence.', 'info');
        return;
      }

      addInstructionToCurrentSequence(item.category, library);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.TRIGGER,
    tooltip: 'Run sequence',
    text: 'Add Execution',
    icon: icons.addExecution,
    onClick: (item, library) => {
      let instructionName;

      const currentApplication = library.currentApplication;
      const currentSequence = currentApplication.currentSequence;
      //get all sequences that are unlocked, are not the main sequence, and are not the current sequence
      const filteredSequences = currentApplication.sequences
        .filter(sequence => {
          return (
            !sequence.getIsLocked()
            && currentApplication.getInitialSequence().getName() != sequence.getName()
            && currentSequence.getName() != sequence.getName()
          );
        });
      if (filteredSequences.length == 0) {
        instructionName = 'terminate';
      }

      addInstructionToCurrentSequence(item.category, library, instructionName);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.SET_VARIABLE,
    tooltip: 'Modify a Variable or an application parameter',
    text: 'Set Variable',
    icon: icons.addVariable,
    onClick: (item, library) => {
      // Disable the "Set Variable" button if there are no vars defined in the application
      const currentApplication = library.currentApplication;
      if (currentApplication.getParametersObject().listParametersNameOfType(['const']).length < 1) {
        widgets.messageBox('You need to add a variable before modifying it', 'info');
        return;
      }

      addInstructionToCurrentSequence(item.category, library);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.CONDITION,
    tooltip: 'Execute child sequences conditionally',
    text: 'Add Condition',
    icon: icons.addCondition,
    onClick: (item, library) => {
      if (!isChildSequenceAvailableForParallelCommand(library)) {
        widgets.messageBox('Add a child sequence before using this instruction category.', 'info');
        return;
      }

      addInstructionToCurrentSequence(item.category, library);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.LOOP,
    tooltip: 'Loop the execution of child sequences',
    text: 'Add Loop',
    icon: icons.addLoop,
    onClick: (item, library) => {
      if (!isChildSequenceAvailableForParallelCommand(library)) {
        widgets.messageBox('Add a child sequence before using this instruction category.', 'info');
        return;
      }

      addInstructionToCurrentSequence(item.category, library);
    }
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.LOG,
    tooltip: 'Output a message to the UI',
    text: 'Add Message',
    icon: icons.addLog,
    onClick: (item, library) => addInstructionToCurrentSequence(item.category, library)
  },
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.ROBOT,
    tooltip: 'Robot path',
    text: 'Add Robot path',
    icon: icons.addRobotSequence,
    disabled: (library: Library) => !isValidRobotConfig(library),
    onClick: (item, library) => addInstructionToCurrentSequence(item.category, library)
  },
];


export const pendantCommandsButtonConfig: ButtonConfig[] = [...baseCommandsButtonConfig];
export const cadCommandsButtonConfig: ButtonConfig[] = [
  ...baseCommandsButtonConfig,
  {
    groupName: 'MachineMotion Commands',
    category: InstructionCategory.ROBOT_LOOP,
    tooltip: 'Robot loop',
    text: 'Add Robot Loop',
    icon: icons.addLoop,
    disabled: (library: Library) => !isValidRobotConfig(library),
    onClick: (item, library) => addInstructionToCurrentSequence(item.category, library)
  }
];
